/** The order spine's four nodes (plan §2.4): Placed · Contact · Bookings · Money, each
 *  with its key datum, and where the order is in its life. Pure — `now` is a parameter.
 *
 *  Where "now" is follows the order's bookings: before the first one starts the order
 *  is placed and its contact known — the current node is Contact and the "now" tick
 *  sits before Bookings; while its bookings run, Bookings is current; once the last one
 *  has ended, only the money is left to settle. The booking spine's rule: the node
 *  before now is the current one.
 *
 *  An order carries no zone of its own (its bookings may span tours in several), so the
 *  spine's times are UTC, named once at its end — never the browser's zone; each booking
 *  page shows its own tour's zone. */
import type { OrderRecordStage } from '@/api/orderRecord';
import type { OrderDetail } from '@/api/orders';
import type { SpineModel } from '@/features/bookings/record/views/spineModel';
import { formatMoney } from '@/features/bookings/overview/common';
import { tripPhase } from '@/features/records/time/tripPhase';
import { formatRelative, formatZoned } from '@/utils/zonedTime';

/** The zone an order's times are shown in. */
export const ORDER_TIMEZONE = 'UTC';

/** Order.ORDER_STATE_CONFIRMED. */
const CONFIRMED = 1;

const TITLES: Record<OrderRecordStage, string> = {
  placed: 'Placed',
  contact: 'Contact',
  bookings: 'Bookings',
  money: 'Money',
};

const STAGES: OrderRecordStage[] = ['placed', 'contact', 'bookings', 'money'];

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function orderSpineModel(d: OrderDetail, now: Date = new Date()): SpineModel<OrderRecordStage> {
  const short = (iso: string, opts: { weekday?: boolean } = {}) =>
    formatZoned(iso, ORDER_TIMEZONE, { zone: false, ...opts, now });
  // Contact until the first booking starts, Bookings while they run, then Money.
  const phase = tripPhase(d.start_datetime, d.end_datetime, now);
  const current = phase === 'active' ? 2 : phase === 'past' ? 3 : 1;

  const placedAt = d.remote_created_ts ?? d.created_ts;
  const placed = {
    datum: placedAt ? short(placedAt, { weekday: false }) : 'Placing time not sent',
    detail:
      [
        d.order_ref ? `Ref ${d.order_ref}` : 'No order ref',
        d.booking_state !== CONFIRMED ? d.booking_state_display : null,
        d.active ? null : 'inactive',
      ]
        .filter(Boolean)
        .join(' · ') || null,
  };

  const c = d.contact;
  const name = [c?.first_name, c?.last_name].filter(Boolean).join(' ');
  const contact = {
    datum: name || c?.email || c?.phone || 'No contact',
    detail: (name ? (c?.email ?? c?.phone) : c?.email ? c.phone : null) || c?.company || null,
  };

  const count = plural(d.booking_count, 'booking', 'bookings');
  let when: string | null = null;
  if (phase === 'future') when = `starts ${formatRelative(d.start_datetime, { now })}`;
  else if (phase === 'active') when = `ends ${formatRelative(d.end_datetime, { now })}`;
  else if (phase === 'past' && (d.end_datetime ?? d.start_datetime)) {
    when = `ended ${formatRelative(d.end_datetime ?? d.start_datetime, { now })}`;
  }
  const bookings = {
    datum: d.start_datetime ? short(d.start_datetime) : d.booking_count > 0 ? 'No start time' : 'No bookings',
    detail: [count, when].filter(Boolean).join(' · '),
  };

  const retail = formatMoney(d.price_retail, d.currency, d.currency_precision);
  const tax = formatMoney(d.tax_amount, d.currency, d.currency_precision);
  const money = {
    datum: retail ?? 'No price sent',
    detail: [d.settlement_method, tax ? `tax ${tax}` : null].filter(Boolean).join(' · ') || null,
  };

  const content: Record<OrderRecordStage, { datum: string; detail: string | null }> = {
    placed,
    contact,
    bookings,
    money,
  };

  return {
    current,
    timezone: ORDER_TIMEZONE,
    nodes: STAGES.map((stage, i) => ({
      stage,
      title: TITLES[stage],
      ...content[stage],
      state: i < current ? 'past' : i === current ? 'current' : 'future',
    })),
  };
}
